import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { API_BASE_URL } from "src/swagger/api.client.generated";
import { InsightCard } from "./nova-insight.model";

/**
 * Real Insight data (FLIP-202). NovaInsightWidgetComponent and NovaInsightModalComponent only
 * know about the InsightCard shape (nova-insight.model.ts) - they have no idea it now comes from
 * a server-side Claude call rather than a hardcoded placeholder.
 *
 * The generation itself is entirely server-side: this calls our own backend, which assembles the
 * user's digest and calls Claude. Per nova-prototype-file-guidance.md §5 the browser must never
 * call Anthropic directly, so nothing here knows about prompts, digests or API keys.
 *
 * Uses the direct-HttpClient + API_BASE_URL pattern rather than the NSwag-generated client,
 * matching NovaInsightSettingsService and CommunityWarmupService - the generated client is built
 * from a live API instance (`npm run nswag`), which isn't practical to regenerate here.
 *
 * FAILURE BEHAVIOUR: returns null, which leaves the widget hidden (its `showWidget` getter
 * already gates on `!!card`). Deliberately NOT falling back to a canned card: every Insight Card
 * makes a claim about this user's own data ("this has come up as a Star several times this
 * week"), so showing a placeholder when the real thing is unavailable would fabricate a personal
 * health observation. Showing nothing is the honest failure mode.
 */
@Injectable({
  providedIn: "root",
})
export class NovaInsightService {
  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {}

  /**
   * The current Insight Card, or null when there is nothing to show.
   *
   * Null covers three cases the caller treats identically: the backend returned 204 (no card
   * worth surfacing), Nova is disabled, or the call failed.
   */
  getCurrentInsightCard(): Observable<InsightCard | null> {
    return this.http
      .get<InsightCard | null>(`${this.apiBaseUrl}/api/Nova/GetCurrentInsight`)
      .pipe(catchError(() => of(null)));
  }

  /**
   * Records that this card was actually shown to the user.
   *
   * Posts the whole card, not just its category. Two server-side readers depend on it: the
   * repetition guard uses the category to avoid repeating a kind of card day after day, and the
   * Companion uses the card's text so Nova in chat can refer back to an observation the person has
   * already read. Sending the category alone would leave chat able to name a topic and nothing
   * about what was actually said.
   *
   * Called by the widget when it renders, not when the card is fetched: the two are no longer the
   * same event, and a card the user never saw must not suppress its category or turn up in chat as
   * something Nova claims to have told them.
   *
   * Fire-and-forget. A failed record means the guard misses one data point, which is not worth
   * surfacing to the user - swallowed the same way a failed fetch is.
   */
  recordInsightShown(card: InsightCard): Observable<void | null> {
    return this.http
      .post<void>(`${this.apiBaseUrl}/api/Nova/RecordInsightShown`, {
        category: card.category,
        expandedTitle: card.expandedTitle,
        snackbarTitle: card.snackbarTitle,
        noticed: card.noticed,
        matters: card.matters,
        question: card.question,
        action: card.action,
      })
      .pipe(catchError(() => of(null)));
  }
}
